import { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { Image } from '@/atoms/Image'
import { useDisclose } from '@/hooks/useDisclose'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary/cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { DavidButton } from './DavidButton'

const imgHero = '/v1760542760/theatrical/david/david-nitzevet-img-01'
const imgHeroPoster = getCloudinaryImageUrl({ path: imgHero, width: 1920 })
const imgDavidLogo = '/v1761067809/theatrical/david/david-logo-color'

const changeChildLifeVideoUrl = 'https://www.youtube-nocookie.com/embed/bkls-zujgd4'

// Animation variants for stagger performance optimization
const titleContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.8, // Base delay after logo animation
      staggerChildren: 0.06, // 60ms between words
    },
  },
}

const wordVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
}

const buttonContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 1.72, // Button start delay
      staggerChildren: 0.1, // Button stagger
    },
  },
}

const buttonVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

interface HeroProps {}

export const Hero = forwardRef<HTMLElement, HeroProps>((_, ref) => {
  const { t } = useTranslate('pif')
  const { isOpen: showVideoModal, onOpen: onOpenVideoModal, onClose: onCloseVideoModal } = useDisclose()

  const line1Words = t('davidHeroLine1', 'join the movement to help').split(' ')
  const line2Words = t('davidHeroLine2', '1 million').split(' ')
  const line3Words = t('davidHeroLine3', 'kids go free December 19').split(' ')

  return (
    <section ref={ref} className="relative min-h-[90svh] w-full" data-name="hero">
      <div className="flex min-h-[90vh] w-full flex-col items-center justify-between self-stretch px-[16px] py-[24px] md:absolute md:left-0 md:top-0 md:p-0">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          {/* Static image fallback */}
          <Image alt="" className="object-cover" src={imgHero} fill sizes="100vw" priority />

          <video
            autoPlay
            loop
            muted
            playsInline
            poster={imgHeroPoster}
            className="absolute size-full max-w-none object-cover"
            style={{ objectPosition: '50% 50%' }}
          >
            <source
              src="https://content.angelstudios.com/david/David_tLoopingVideo_l-en_wNOCAPS_16x9_f23_y20251013_iDG.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-[rgba(0,0,0,0.4)]" />
        </div>

        <div
          className="mt-auto flex w-full flex-col content-stretch items-start gap-[40px] px-[8px] md:absolute md:bottom-[64px] md:left-[64px] md:w-[779px] md:px-0"
          data-name="hero-content"
        >
          <div
            className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[22px]"
            data-name="hero-copy"
          >
            {/* DAVID Logo */}
            <motion.div
              className="relative h-[51px] w-[200px] shrink-0 md:h-[66px] md:w-[260px]"
              data-name="david-logo"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0,
                duration: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <Image alt="DAVID" className="object-contain" src={imgDavidLogo} fill sizes="260px" priority />
              </div>
            </motion.div>

            {/* Title Copy with staggered word animations */}
            <motion.div
              className="font-angel-royal relative flex w-full shrink-0 flex-col content-stretch items-start gap-1 text-nowrap lowercase not-italic tracking-[0.5px] text-white"
              data-name="title-copy"
              variants={titleContainerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Line 1 */}
              <div className="relative flex shrink-0 flex-wrap justify-start gap-x-[0.25em] text-[32px] leading-[0] [text-shadow:rgba(0,0,0,0.3)_1px_2px_0px] md:text-[64px]">
                {line1Words.map((word, index) => (
                  <motion.span
                    key={`line1-${index}`}
                    variants={wordVariants}
                    className="leading-[24px] md:leading-[48px]"
                  >
                    {word}
                  </motion.span>
                ))}
              </div>

              {/* Line 2 */}
              <div className="relative flex shrink-0 flex-wrap justify-start gap-x-[0.15em] text-[110px] leading-[72px] [text-shadow:rgba(0,0,0,0.3)_1px_2px_0px] md:text-[240px] md:leading-[140px]">
                {line2Words.map((word, index) => (
                  <motion.span key={`line2-${index}`} variants={wordVariants}>
                    {word}
                  </motion.span>
                ))}
              </div>

              {/* Line 3 */}
              <div className="relative flex shrink-0 flex-wrap justify-start gap-x-[0.25em] text-[32px] leading-[24px] [text-shadow:rgba(0,0,0,0.3)_1px_2px_0px] md:text-[64px] md:leading-[48px]">
                {line3Words.map((word, index) => (
                  <motion.span key={`line3-${index}`} variants={wordVariants}>
                    {word}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Buttons with stagger animation */}
          <motion.div
            className="relative flex w-full shrink-0 flex-col content-stretch items-start justify-center gap-2 md:w-auto md:flex-row md:items-center"
            data-name="button-set"
            variants={buttonContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={buttonVariants} className="w-full md:w-auto">
              <DavidButton variant="orange" openPifOverlay className="w-full md:w-auto">
                {t('payItForward', 'Pay it Forward')}
              </DavidButton>
            </motion.div>

            <motion.div variants={buttonVariants} className="w-full md:w-auto">
              <DavidButton variant="white" onClick={onOpenVideoModal} className="w-full md:w-auto">
                {t('watchSpecialMessage', 'Watch Special Message')}
              </DavidButton>
            </motion.div>
          </motion.div>

          <YoutubeVideoModal
            youTubeUrl={changeChildLifeVideoUrl}
            showModal={showVideoModal}
            handleClose={onCloseVideoModal}
          />
        </div>
      </div>
    </section>
  )
})

Hero.displayName = 'Hero'
